import { Container, type Sprite, type Texture } from 'pixi.js';
import { getTexture, makeSprite } from '@/assets/resolver';
import { COLORS } from '@/config/colors';
import { MAPS } from '@/config/maps';
import type { World } from '@/scene/world';

/** Icon frames of `ui/objective-arrow` (frame 0 is the arrow itself). */
const ICON = { boss: 1, gate: 2, enemy: 3, portal: 4 } as const;

/**
 * Off-screen objective arrow (docs/01 objective arrow): a chevron at the screen
 * edge pointing at where to go — the open gate / boss chamber on a route,
 * the boss in a big field, the portal after the kill, or the last enemies
 * of a wave when every one of them is off-screen. Icon-first, no words. An
 * open route gate also gets a bobbing marker when it IS on screen.
 */
class ObjectiveArrow {
  readonly view = new Container();
  private readonly arrow: Sprite;
  private readonly icon: Sprite;
  private readonly target = { x: 0, y: 0 };
  /** Icon textures by frame (resolved once — no per-frame key lookups) */
  private readonly icons: Texture[] = [];

  constructor() {
    this.arrow = makeSprite('ui/objective-arrow', 0);
    this.icon = makeSprite('ui/objective-arrow', ICON.gate);
    for (let f = 0; f <= ICON.portal; f++) this.icons.push(getTexture('ui/objective-arrow', f));
    this.view.addChild(this.arrow, this.icon);
    this.view.visible = false;
  }

  sync(world: World): void {
    const cam = world.camera;
    const sw = cam.viewW;
    const sh = cam.viewH;
    const kind = this.pick(world);
    if (kind < 0 || sw <= 0) {
      this.view.visible = false;
      return;
    }
    const A = MAPS.arrow;
    const t = performance.now() / 1000;
    const sx = this.target.x - cam.centerX + sw / 2;
    const sy = this.target.y - cam.centerY + sh / 2;
    const onScreen = sx > A.margin && sx < sw - A.margin && sy > A.marginTop && sy < sh - A.margin;
    const waypoint = kind === ICON.gate || (kind === ICON.boss && world.arena.waypoint.active);
    if (onScreen && !waypoint) {
      this.view.visible = false;
      return;
    }
    this.view.visible = true;
    this.icon.texture = this.icons[kind]!;
    const tint = kind === ICON.boss ? COLORS.dangerRed : kind === ICON.enemy ? COLORS.dangerOrange : COLORS.playerCyan;
    this.arrow.tint = tint;
    const pulse = 1 + A.pulse * Math.sin(t * A.pulseHz * Math.PI * 2);
    if (onScreen) {
      // On-screen waypoint: a bobbing down-arrow above the spot
      const bob = Math.sin(t * A.bobHz * Math.PI * 2) * A.bob;
      this.arrow.rotation = Math.PI / 2;
      this.arrow.position.set(sx, sy - A.markerLift + bob);
      this.icon.position.set(sx, sy - A.markerLift - A.iconGap + bob);
      this.arrow.scale.set(A.scale * pulse);
      this.icon.scale.set(A.iconScale);
      return;
    }
    // Edge arrow: ray from the inset box centre to the target, clamped inside
    const cx = sw / 2;
    const cy = (A.marginTop + sh - A.margin) / 2;
    const dx = sx - cx;
    const dy = sy - cy;
    const hx = sw / 2 - A.margin;
    const hy = (sh - A.margin - A.marginTop) / 2;
    const k = Math.min(hx / Math.max(1e-3, Math.abs(dx)), hy / Math.max(1e-3, Math.abs(dy)));
    const ax = cx + dx * k;
    const ay = cy + dy * k;
    const ang = Math.atan2(dy, dx);
    this.arrow.rotation = ang;
    this.arrow.position.set(ax, ay);
    this.arrow.scale.set(A.scale * pulse);
    this.icon.position.set(ax - Math.cos(ang) * A.iconGap, ay - Math.sin(ang) * A.iconGap);
    this.icon.scale.set(A.iconScale);
  }

  /** Writes the target; @returns the icon frame, or -1 for no objective. */
  private pick(world: World): number {
    const { portal, boss, arena, minions, player } = world;
    if (portal.active) return this.set(portal.x, portal.y, ICON.portal);
    if (boss && !boss.dead && boss.view.visible) return this.set(boss.x, boss.y, ICON.boss);
    const wp = arena.waypoint;
    if (wp.active) return this.set(wp.x, wp.y, wp.chamber ? ICON.boss : ICON.gate);
    // Wave leftovers: only when even the nearest one is off screen
    const m = minions.count > 0 ? minions.nearest(player.x, player.y) : null;
    const cam = world.camera;
    if (m && (Math.abs(m.x - cam.centerX) > cam.viewW / 2 || Math.abs(m.y - cam.centerY) > cam.viewH / 2)) {
      return this.set(m.x, m.y, ICON.enemy);
    }
    return -1;
  }

  private set(x: number, y: number, icon: number): number {
    this.target.x = x;
    this.target.y = y;
    return icon;
  }
}

const arrows = new WeakMap<Container, ObjectiveArrow>();

/** HUD hook (one line in hud-sync): keep the arrow on `layer` in sync. */
export function syncObjective(layer: Container, world: World): void {
  let a = arrows.get(layer);
  if (!a) {
    a = new ObjectiveArrow();
    arrows.set(layer, a);
    layer.addChild(a.view);
  }
  a.sync(world);
}
